import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Box, Stack, Typography, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, useTheme,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';
import { CMButton } from '../../containers/styledComponents';
import { listExecutions, retryExecution, cancelExecution } from '../../redux/actions/ingestion/executions';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';

const rel = (d) => { try { return d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : '—'; } catch (_e) { return '—'; } };
const num = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');
const dur = (s) => (s == null ? '—' : s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`);
const STATUS_TONE = { completed: 'success', processing: 'info', queued: 'warning', failed: 'error', cancelled: 'neutral', partial: 'warning' };

const isFileExecution = (e) => {
  const c = (e.connector_type || e.source?.connector_type || '').toLowerCase();
  const t = (e.trigger_type || '').toLowerCase();
  return c === 'file' || c === 's3' || t.includes('upload') || e.source_id === 'file-upload';
};

const fileName = (e) =>
  e.source_config_snapshot?.name
  || (e.job_config_snapshot?.file_path || '').split('/').pop()
  || 'Uploaded file';

const UploadHistoryPage = ({ executions, pagination, isLoading, listExecutions, retryExecution, cancelExecution }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [errorDialog, setErrorDialog] = useState(null);

  const fetchPage = useCallback((p, rpp) => {
    listExecutions({ connectorType: 'file', page: p + 1, perPage: rpp, sortColumn: 'started_at', sortDirection: 'desc' });
  }, [listExecutions]);

  useEffect(() => { fetchPage(0, rowsPerPage); }, [fetchPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live (no polling): refresh the current page as uploads progress
  // (queued -> processing -> completed/failed, rows + duration).
  useRealtimeRefresh(eventPrefix('ingestion.execution.'), () => fetchPage(page, rowsPerPage));

  const rows = useMemo(() => (executions || []).filter(isFileExecution), [executions]);

  const columns = [
    {
      key: 'identity', label: 'Upload', width: 130,
      render: (e) => <EntityCell id={e.execution_id} tone={STATUS_TONE[e.status] || 'neutral'} label={e.status} />,
    },
    {
      key: 'file', label: 'File',
      render: (e) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <FileUploadIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap title={fileName(e)}>{fileName(e)}</Typography>
        </Stack>
      ),
    },
    { key: 'rows', label: 'Rows', width: 110, align: 'right', render: (e) => <Typography sx={{ fontSize: 13 }}>{num(e.stats?.rows_inserted ?? e.stats?.rows_processed)}</Typography> },
    { key: 'duration', label: 'Duration', width: 100, align: 'right', render: (e) => <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{dur(e.stats?.duration_seconds ?? e.duration_seconds)}</Typography> },
    { key: 'started', label: 'Uploaded', width: 140, render: (e) => <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{rel(e.started_at)}</Typography> },
    {
      key: 'detail', label: 'Detail', width: 240, stopRowClick: true,
      cellSx: { maxWidth: 240 },
      render: (e) => e.error?.message
        ? (
          <Tooltip title="Click to see full error" arrow placement="top">
            <Typography
              onClick={() => setErrorDialog({ id: e.execution_id, file: fileName(e), message: e.error.message })}
              sx={{
                fontSize: 12, color: theme.palette.error.main, cursor: 'pointer',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240,
                textDecoration: 'underline', textDecorationStyle: 'dotted',
                '&:hover': { color: theme.palette.error.dark },
              }}>
              {e.error.message}
            </Typography>
          </Tooltip>
        )
        : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>—</Typography>,
    },
    {
      key: 'actions', label: '', width: 56, align: 'right', stopRowClick: true,
      render: (e) => {
        const items = [];
        if (['failed', 'cancelled', 'partial', 'completed'].includes(e.status)) items.push({ label: 'Retry', icon: ReplayIcon, onClick: () => retryExecution(e.execution_id) });
        if (['queued', 'processing'].includes(e.status)) items.push({ label: 'Cancel', icon: CloseIcon, danger: true, onClick: () => cancelExecution(e.execution_id) });
        return items.length ? <RowActions onOpen={null} items={items} /> : null;
      },
    },
  ];

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <PageBreadcrumbs items={[
          { text: 'Data Sources', link: '/datasources', icon: <StorageIcon /> },
          { text: 'Uploads' },
        ]} />
        <CMButton startIcon={<FileUploadIcon />} size="small" onClick={() => navigate('/datasources/connect')}>
          Upload a file
        </CMButton>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 0.5 }}>
        Every file you've imported, with rows ingested and status.
      </Typography>

      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        getRowKey={(e) => e.execution_id}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={pagination?.total ?? rows.length}
        onPageChange={(p) => { setPage(p); fetchPage(p, rowsPerPage); }}
        onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); fetchPage(0, rpp); }}
        emptyState={{
          icon: <FileUploadIcon />, title: 'No uploads yet',
          message: 'Upload a CSV, Parquet, Excel or JSON file to import data.',
          action: <CMButton startIcon={<FileUploadIcon />} size="small" onClick={() => navigate('/datasources/connect')}>Upload a file</CMButton>,
        }}
      />

      <Dialog open={!!errorDialog} onClose={() => setErrorDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Import failed
          {errorDialog?.file && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{errorDialog.file}</Typography>}
        </DialogTitle>
        <DialogContent>
          <Box component="pre" sx={{
            m: 0, p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.6,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            border: `1px solid ${theme.palette.divider}`, color: theme.palette.error.main,
          }}>
            {errorDialog?.message}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { try { navigator.clipboard.writeText(errorDialog?.message || ''); } catch (_e) { /* ignore */ } }} sx={{ textTransform: 'none' }}>Copy</Button>
          <Button variant="contained" onClick={() => setErrorDialog(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  executions: state.ingestion_executions?.executions || [],
  pagination: state.ingestion_executions?.pagination || {},
  isLoading: state.ingestion_executions?.isLoading || false,
});

export default connect(mapStateToProps, { listExecutions, retryExecution, cancelExecution })(UploadHistoryPage);
